<template>
  <div class="product-chooser">
    <!-- Loading View -->
<div v-if="loading" class="loading-container">
  <div class="spinner"></div>
  <p class="intro">Loading your account info...</p>
</div>

    <!-- Show Product Options -->
    <div v-else-if="shouldShowProducts">
      <h1>Get Access to PathTracer</h1>
      <p class="intro">
        Choose a plan below to unlock transaction analysis tools powered by visuals and reports.
      </p>
      <p
        class="intro"
        v-if="creditBalance !== null && !isSubscriptionUser && !isInfiniteAccess"
      >
        You currently have <strong>{{ creditBalance }}</strong> credit{{ creditBalance !== 1 ? 's' : '' }} remaining.
      </p>
 <p v-if="atMaxSubCap" class="status-msg">
  You’re at the maximum prepaid subscription length ({{ maxSubMonths }} months). Try again closer to your expiry.
</p>
      <div v-if="subscriptionExpiresAt && !isSubscriptionUser">
        <p class="status-msg">
          ⏳ Your subscription expired on <strong>{{ formatDate(subscriptionExpiresAt) }}</strong>.
        </p>
      </div>

      <div class="product-card" v-for="product in filteredProducts" :key="product.id">
        <h2>{{ product.name }}</h2>
        <p class="price">{{ product.price }}</p>
        <p class="description">{{ product.description }}</p>
        <div class="button-wrapper">
   <button
  @click="checkout(product)"
  :disabled="product.id==='subscription' && atMaxSubCap"
  :title="product.id==='subscription' && atMaxSubCap ? `You can only hold ${maxSubMonths} months at a time.` : ''"
  :data-track="'product_checkout_click'"
  :data-track-props="JSON.stringify({ priceId: product.priceId, productId: product.id })"
>
  {{ getButtonLabel(product) }}
</button>

        </div>
        <p> </p>
    <small v-if="product.id==='subscription'" class="hint">
  <center>You can hold up to {{ maxSubMonths }} months of prepaid time.</center>
</small>
      </div>

      <p v-if="status" class="status-msg">{{ status }}</p>
    </div>

    <!-- Already Subscribed / Infinite Access View -->
    <div v-else>
      <img
        src="https://cdn-icons-png.flaticon.com/512/190/190411.png"
        alt="Checkmark"
        style="width: 64px; margin: 20px auto;"
      />

      <div v-if="isSubscriptionUser">
        <h1>You're on a Subscription</h1>
        <p class="intro">
          You already have full access to PathTracer tools.<br />
          Your subscription expires in <strong>{{ subscriptionDays }}</strong> day{{ subscriptionDays !== 1 ? 's' : '' }}.
          You can extend your subscription below.
        </p>
      </div>

      <div v-else-if="hasExpiredSubscription">
        <h1>Subscription Expired</h1>
        <p class="intro">
          Your subscription expired on <strong>{{ formatDate(subscriptionExpiresAt) }}</strong>.<br />
          You can either renew your subscription or purchase credits to continue using PathTracer.
        </p>
      </div>

      <div v-else>
        <h1>No Purchase Needed</h1>
        <p class="intro">
          You already have full access to all PathTracer tools.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

import { logEvent } from '@/analytics'; // ← add this

const MAX_SUB_MONTHS = 6;

function monthsBetweenInclusive(now, future) {
  // Count whole months between "now" and "future" (floor)
  const a = new Date(now.getFullYear(), now.getMonth(), 1);
  const b = new Date(future.getFullYear(), future.getMonth(), 1);
  const months = (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
  return Math.max(0, months);
}


export default {
  name: 'ProductChooserTest',
  data() {
    return {
      status: '',
      maxSubMonths: MAX_SUB_MONTHS,
      creditBalance: null,
      isSubscriptionUser: false,
      isUnlimitedUser: false,
    subscriptionExpiresAt: null,
      role: null,
      subscriptionDays: null,
    loading: true, 
      hasLoggedImpression: new Set(),   // ← track per priceId
      io: null,                         // ← IntersectionObserver
    products: [
    
  {
    id: 'credits',
    name: 'PathTracer Credits Package (50 Credits)',
    price: '$50',
    description: 'PathTracer credits can be used to request an analysis on a transaction hash using the analysis tool.   Includes a visualization and a summary report which totals amounts based on categories of likely destinations, if found.  This report does not include deposit address or transaction hash details.  Credits are valid for up to two years after purchase.',
    priceId: 'price_1SXPuGFoYPmMTGQ9vEuJ73hP' 
  },
  {
    id: 'credits',
    name: 'PathTracer Credits Package (100 Credits)',
    price: '$100',
    description: 'PathTracer credits can be used to request an analysis on a transaction hash using the analysis tool.   Includes a visualization and a summary report which totals amounts based on categories of likely destinations, if found.  This report does not include deposit address or transaction hash details.  Credits are valid for up to two years after purchase.',
    priceId: 'price_1SXPojFoYPmMTGQ9Zs4spqZ0' 
  },
  {
    id: 'credits',
    name: 'PathTracer Credits Package (250 Credits)',
    price: '$250',
    description: 'PathTracer credits can be used to request an analysis on a transaction hash using the analysis tool.   Includes a visualization and a summary report which totals amounts based on categories of likely destinations, if found.  This report does not include deposit address or transaction hash details.  Credits are valid for up to two years after purchase.',
    priceId: 'price_1SXPuzFoYPmMTGQ92lTA4meK' 
  },
  {
    id: 'credits',
    name: 'PathTracer Credits Package (500 Credits)',
    price: '$400',
    description: 'PathTracer credits can be used to request an analysis on a transaction hash using the analysis tool.   Includes a visualization and a summary report which totals amounts based on categories of likely destinations, if found.  This report does not include deposit address or transaction hash details.  Credits are valid for up to two years after purchase.',
    priceId: 'price_1SXPvOFoYPmMTGQ9ieUTOoz4' 
  },
  {
    id: 'credits',
    name: 'PathTracer Credits Package (1000 Credits)',
    price: '$750',
    description: 'PathTracer credits can be used to request an analysis on a transaction hash using the analysis tool.   Includes a visualization and a summary report which totals amounts based on categories of likely destinations, if found.  This report does not include deposit address or transaction hash details.  Credits are valid for up to two years after purchase.',
    priceId: 'price_1SXPvwFoYPmMTGQ9hJSPxTsw' 
  },
  {
    id: 'credits',
    name: 'PathTracer Credits Package (2500 Credits)',
    price: '$1500',
    description: 'PathTracer credits can be used to request an analysis on a transaction hash using the analysis tool.   Includes a visualization and a summary report which totals amounts based on categories of likely destinations, if found.  This report does not include deposit address or transaction hash details.  Credits are valid for up to two years after purchase.',
    priceId: 'price_1SXPwRFoYPmMTGQ9Mw3zuoxW' 
  },
    // test products
    /*
  {
    id: 'credits',
    name: 'PathTracer Credits Package (50 Credits)',
    price: '$50',
    description: 'PathTracer credits can be used to request an analysis on a transaction hash using the analysis tool.   Includes a visualization and a summary report which totals amounts based on categories of likely destinations, if found.  This report does not include deposit address or transaction hash details.  Credits are valid for up to two years after purchase.',
    priceId: 'price_1SXPuGFoYPmMTGQ9vEuJ73hP' 
  },
  {
    id: 'credits',
    name: 'PathTracer Credits Package (100 Credits)',
    price: '$100',
    description: 'PathTracer credits can be used to request an analysis on a transaction hash using the analysis tool.   Includes a visualization and a summary report which totals amounts based on categories of likely destinations, if found.  This report does not include deposit address or transaction hash details.  Credits are valid for up to two years after purchase.',
    priceId: 'price_1SXPojFoYPmMTGQ9Zs4spqZ0' 
  },
  {
    id: 'credits',
    name: 'PathTracer Credits Package (250 Credits)',
    price: '$250',
    description: 'PathTracer credits can be used to request an analysis on a transaction hash using the analysis tool.   Includes a visualization and a summary report which totals amounts based on categories of likely destinations, if found.  This report does not include deposit address or transaction hash details.  Credits are valid for up to two years after purchase.',
    priceId: 'price_1SXPuzFoYPmMTGQ92lTA4meK' 
  },
  {
    id: 'credits',
    name: 'PathTracer Credits Package (500 Credits)',
    price: '$400',
    description: 'PathTracer credits can be used to request an analysis on a transaction hash using the analysis tool.   Includes a visualization and a summary report which totals amounts based on categories of likely destinations, if found.  This report does not include deposit address or transaction hash details.  Credits are valid for up to two years after purchase.',
    priceId: 'price_1SXPvOFoYPmMTGQ9ieUTOoz4' 
  },
  {
    id: 'credits',
    name: 'PathTracer Credits Package (1000 Credits)',
    price: '$750',
    description: 'PathTracer credits can be used to request an analysis on a transaction hash using the analysis tool.   Includes a visualization and a summary report which totals amounts based on categories of likely destinations, if found.  This report does not include deposit address or transaction hash details.  Credits are valid for up to two years after purchase.',
    priceId: 'price_1SXPvwFoYPmMTGQ9hJSPxTsw' 
  },
  {
    id: 'credits',
    name: 'PathTracer Credits Package (2500 Credits)',
    price: '$1500',
    description: 'PathTracer credits can be used to request an analysis on a transaction hash using the analysis tool.   Includes a visualization and a summary report which totals amounts based on categories of likely destinations, if found.  This report does not include deposit address or transaction hash details.  Credits are valid for up to two years after purchase.',
    priceId: 'price_1SXPwRFoYPmMTGQ9Mw3zuoxW' 
  },

  */
  /*
  {
    id: 'credits',
    name: 'PathTracer Credits Package (10 Credits)',
    price: '$100',
    description: 'PathTracer credits can be used to request an analysis on a transaction hash using the analysis tool.   Includes a visualization and a summary report which totals amounts based on categories of likely destinations, if found.  This report does not include deposit address or transaction hash details.  Credits are valid for up to two years after purchase.',
    priceId: 'price_1RmdxzFoYPmMTGQ9Kxb2xbQ3' 
  },
  {
    id: 'credits',
    name: 'PathTracer Credits Package (10 Credits)',
    price: '$250',
    description: 'PathTracer credits can be used to request an analysis on a transaction hash using the analysis tool.   Includes a visualization and a summary report which totals amounts based on categories of likely destinations, if found.  This report does not include deposit address or transaction hash details.   Credits are valid for up to two years after purchase.',
    priceId: 'price_1SC7n4FoYPmMTGQ9a5PGgydN' 
  },
  {
    id: 'credits',
    name: 'PathTracer Credits Package (100 Credits)',
    price: '$1000',
    description: 'PathTracer credits can be used to request an analysis on a transaction hash using the analysis tool.   Includes a visualization and a summary report which totals amounts based on categories of likely destinations, if found.  This report does not include deposit address or transaction hash details.  Credits are valid for up to two years after purchase.',
    priceId: 'price_1SC7psFoYPmMTGQ9stms6iny' 
  },
  {
    id: 'subscription',
    name: 'PathTracer Subscription (30 days)',
    price: '$500 / 30 days',
    description: 'Unlock access to PathTracer\'s analysis tools for 30 days. Reasonable usage limits apply.',
    priceId: 'price_1RkA86FoYPmMTGQ9qdjTfF9a' 
  },
  {
    id: 'subscription',
    name: 'PathTracer Subscription (10 days)',
    price: '$300 / 10 days',
    description: 'Unlock access to PathTracer\'s analysis tools for 10 days. Reasonable usage limits apply.',
    priceId: 'price_1SC7fEFoYPmMTGQ9deWkLuh2' 
  },
  {
    id: 'linkcheck-credits',
    name: 'PathTracer LinkCheck 50 Credits Bundle',
    price: '$100 / 50 credits',
    description: 'Grants access to 50 PathTracer LinkCheck credits, for looking up transaction hashes to see if their paths link to notable addresses. Using a credit on a supported hash will produce a text report, outlining if any match was found, as well as explanation and informational trace details. Please allow up to 24 hours for processing requests.  Credits are valid for up to two years after purchase.',
    priceId: 'price_1SMBeTFoYPmMTGQ9mdfqtICE' 
  }*/

  
]

    };
  },

   mounted() {
    // Observe product card impressions (once per priceId)
    this.$nextTick(() => {
      const opts = { root: null, rootMargin: '0px', threshold: 0.25 };
      this.io = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const el = entry.target;
          const priceId = el.getAttribute('data-price-id');
          if (!priceId || this.hasLoggedImpression.has(priceId)) continue;

          const product = this.products.find(p => p.priceId === priceId);
          if (product) {
            logEvent('product_list_impression', {
              id: product.id,
              priceId: product.priceId,
              name: product.name,
              price: product.price
            });
            this.hasLoggedImpression.add(priceId);
          }
        }
      }, opts);

      // Attach observer to currently rendered cards
      this.$el.querySelectorAll('.product-card').forEach(card => this.io.observe(card));
    });
  },
  beforeUnmount() {
    if (this.io) { this.io.disconnect(); this.io = null; }
  },

  computed: {

     monthsAheadBooked() {
    if (!this.subscriptionExpiresAt) return 0;
    const now = new Date();
    const expiry = new Date(this.subscriptionExpiresAt);
    if (expiry <= now) return 0;
    return monthsBetweenInclusive(now, expiry);
  },

  // How many months are still allowed to purchase before reaching the cap
  monthsRemainingAllowance() {
    const remaining = MAX_SUB_MONTHS - this.monthsAheadBooked;
    return Math.max(0, remaining);
  },

  // Is the user already at/over the cap?
  atMaxSubCap() {
    return this.monthsRemainingAllowance <= 0;
  },

  // Should subscription be shown as a purchasable product?
  // - If at cap → hide it (or you can show it disabled; see below)
  filteredProducts() {
    if (this.isInfiniteAccess) return [];

    const base = (() => {
      if (this.hasExpiredSubscription) return this.products;             // renew or credits
      if (this.isSubscriptionUser) return this.products.filter(p => p.id === 'subscription');
      return this.products;
    })();

    // Hide subscription when at cap
    return base.filter(p => !(p.id === 'subscription' && this.atMaxSubCap));
  },
  hasExpiredSubscription() {
    if (!this.subscriptionExpiresAt) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(this.subscriptionExpiresAt);
    expiry.setHours(0, 0, 0, 0);
    return expiry < today;
  },

    isInfiniteAccess() {
      return this.role === 'admin' || this.role === 'moderator' || this.isUnlimitedUser;
    },
    shouldShowProducts() {
      // Only show products if the user doesn't have unlimited access
      return !this.isInfiniteAccess;
    },



  },
  created() {
    logEvent('product_page_view', { testMode: true });


    const token = localStorage.getItem('token');
    if (!token) {
      this.status = 'You must be logged in.';
      return;
    }

    axios.get('/etherscan/get_credits.php', {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(res => {
        this.loading = false;
      if (res.data.status === 'success') {
        this.creditBalance = res.data.balance;
     this.subscriptionExpiresAt = res.data.subscription_expires_at;

const expires = this.subscriptionExpiresAt ? new Date(this.subscriptionExpiresAt) : null;
const isExpired = !expires || expires <= new Date();

this.isSubscriptionUser = !!res.data.is_subscription_user && !isExpired;

        this.isUnlimitedUser = !!res.data.is_unlimited_user;
        this.role = res.data.role;
        this.subscriptionDays = res.data.subscriptionDays || 0;


          // Emit account state snapshot
        logEvent('product_account_status_loaded', {
          balance: this.creditBalance,
          isSubscriptionUser: this.isSubscriptionUser,
          isUnlimitedUser: this.isUnlimitedUser,
          role: this.role,
          subscriptionDays: this.subscriptionDays,
          subscriptionExpiresAt: this.subscriptionExpiresAt,
          hasExpiredSubscription: this.hasExpiredSubscription
        });


      } else {
        this.status = 'Failed to load account status.';
      }
    })
    .catch(err => {
        this.loading = false;

      console.error("Error fetching account data:", err);
      this.status = 'Error checking your account access.';
    });
  },
  methods: {
  getButtonLabel(product) {
    if (product.id === 'subscription') {
      if (this.atMaxSubCap) return 'Max extension reached';
      if (this.subscriptionExpiresAt && !this.isSubscriptionUser) return 'Renew Subscription';
      if (this.isSubscriptionUser) return 'Extend Subscription';
      return 'Buy Subscription';
    }
    return 'Buy Now';
  }
,
formatDate(isoString) {
  if (!isoString) return 'N/A';
  return new Date(isoString).toLocaleString();
}
,
     async checkout(product) {

      // Click is already captured by data-track + global delegate.
      // Emit a richer event here as well (keeps working even if delegate not mounted).
      logEvent('product_checkout_click', { priceId: product.priceId, productId: product.id });

 if (product.id === 'subscription' && this.atMaxSubCap) {
  this.status = `You’re already prepaid ${this.maxSubMonths} months out.`;
  return;
}
      this.status = 'Redirecting to Stripe...';
      try {
        const res = await axios.post('/stripe/create_checkout_test.php',
          { priceId: product.priceId },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );

        if (res.data.url) {
          logEvent('product_checkout_redirect_ok', { priceId: product.priceId, productId: product.id });
          window.location.href = res.data.url; // last line
        } else {
          this.status = 'Checkout failed. Try again.';
          logEvent('product_checkout_redirect_error', {
            priceId: product.priceId, productId: product.id, message: 'missing_url'
          });
        }
      } catch (err) {
        console.error('Checkout error:', err);
        this.status = 'Error launching checkout.';
        logEvent('product_checkout_redirect_error', {
          priceId: product.priceId, productId: product.id, message: String(err?.message || err)
        });
      }
    }

  }
};
</script>

<style scoped>
.product-chooser {
  max-width: 900px;
  margin: 0 auto;
  padding: 30px;
  text-align: center;
  background: linear-gradient(180deg, #f3f6fb 0%, #ffffff 100%);
  border-radius: 12px;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.05);
}

h1 {
  font-size: 28px;
  margin-bottom: 10px;
  color: #2c3e50;
}

.intro {
  font-size: 17px;
  color: #555;
  margin-bottom: 30px;
}

.product-card {
  background: #fff;
  border-radius: 12px;
  padding: 25px;
  margin: 20px auto;
  max-width: 500px;
  box-shadow: 0 6px 12px rgba(103, 114, 229, 0.12);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  text-align: left;
}

.product-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(103, 114, 229, 0.25);
}

.product-card h2 {
  font-size: 20px;
  color: #34495e;
  margin-bottom: 10px;
}

.price {
  font-size: 18px;
  font-weight: bold;
  color: #2c3e50;
  margin-bottom: 8px;
}

.description {
  font-size: 14px;
  color: #555;
  margin-bottom: 16px;
}

button {
  background: linear-gradient(135deg, #6772e5, #5469d4);
  color: white;
  padding: 12px 24px;
  font-size: 15px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s ease;
}

button:hover {
  background: linear-gradient(135deg, #5a63e0, #4a58cc);
  box-shadow: 0 4px 10px rgba(84, 105, 212, 0.3);
}

.status-msg {
  margin-top: 30px;
  font-weight: bold;
  color: #e67e22;
}
.button-wrapper {
  display: flex;
  justify-content: center;
}
@media (max-width: 600px) {
  .product-card {
    padding: 18px;
  }


  button {
    width: 100%;
  }
}
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 0;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 6px solid #ccc;
  border-top-color: #5469d4;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.hint { display:block; margin-top:6px; color:#777; font-size:12px; }
button:disabled { opacity: .6; cursor: not-allowed; }



</style>
